<script setup>
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import formatPrice from '@/js/functions/formatPrice.js';
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js';

// Register necessary Chart.js components
ChartJS.register(Title, Tooltip, Legend, ArcElement);

const props = defineProps({
    leasing_price: Object,
    contract_type: String,
});

const chartOptions = {
    responsive: true,
    plugins: {
        legend: {
            display: false,
        },
    },
};

const backgroundColors = {
    bike: '#FF6384',
    entretien: '#36A2EB',
    insurance: '#FFCE56',
    extensionWarranty: '#FF6384',
    interest: '#FF4365',
    managementFee: '#2A80C9',
    rc: '#E4B123',
};

// Compute `chartData` reactively so it updates when `props.bike` changes
const chartData = computed(() => {
    if (!props.leasing_price) {
        return { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    }

    const labels = [
        'Vélo',
        'Entretiens',
        'Assurance',
        'Extension de garantie',
        'Financement',
        'Frais de gestion',
    ];

    const data = [
        props.leasing_price.tvac.bike || 0,
        props.leasing_price.tvac.entretien || 0,
        props.leasing_price.tvac.insurance || 0,
        props.leasing_price.tvac.extensionWarranty || 0,
        props.leasing_price.tvac.interest || 0,
        props.leasing_price.tvac.managementFee || 0,
    ];

    const backgroundColor = [
        backgroundColors.bike,
        backgroundColors.entretien,
        backgroundColors.insurance,
        backgroundColors.extensionWarranty,
        backgroundColors.interest,
        backgroundColors.managementFee,
    ];

    if (props.leasing_price.tvac.rc > 0) {
        labels.push('Assurance RC');
        data.push(props.leasing_price.tvac.rc);
        backgroundColor.push(backgroundColors.rc);
    }

    return {
        labels,
        datasets: [
            {
                label: 'Leasing Composition',
                data,
                backgroundColor,
            },
        ],
    };
});
</script>

<template>
    <!-- Right Block: Doughnut Chart -->
    <div class="chart-container align-center">
        <div class="text-center">
            <strong>
                {{
                    $translate(
                        'public.shop.home.bikes.card.details.leasing.price_leasing_b2c',
                    )
                }}
                :</strong
            >
            <span
                class="opacity-50 line-through text-gray-600 mt-4"
                v-if="leasing_price?.tvac_full"
            >
                {{
                    formatPrice(leasing_price?.tvac_full) +
                    '/' +
                    $translate(
                        'public.shop.home.bikes.card.details.leasing.month',
                    )
                }} </span
            ><br />

            <p class="font-bold">
                {{
                    formatPrice(leasing_price.tvac?.leasingPrice) +
                    '/' +
                    $translate(
                        'public.shop.home.bikes.card.details.leasing.month',
                    )
                }}
                TVAC
            </p>
        </div>

        <Doughnut
            :data="chartData"
            :options="chartOptions"
            class="center mx-auto mt-8"
        />
    </div>
    <div>
        <div class="mb-4">
            <strong>Composition du prix (à titre indicatif)</strong>
        </div>
        <div v-for="(price, index) in leasing_price?.tvac" :key="index">
            <div class="flex w-full" v-if="price > 0">
                <div
                    :style="{
                        backgroundColor: backgroundColors[index],
                    }"
                    class="h-4 w-4 mt-2 mr-4"
                ></div>

                <p
                    class="text-black text-sm font-normal pt-2 pb-2 inline"
                    v-if="
                        index != 'leasingPrice' &&
                        index != 'residualValueAmount'
                    "
                >
                    <strong
                        >{{ price }}
                        <span v-if="contract_type == 'annual_leasing'"
                            >€/an</span
                        ><span v-else>€/mois</span></strong
                    >

                    <span v-if="index == 'bike'">
                        pour le vélo. Il s'agit du montant du vélo divisé par la
                        durée du contrat</span
                    >
                    <span v-else-if="index == 'entretien'">
                        pour l'entretien. Il s'agit du budget d'entretien annuel
                        divisé par 12</span
                    >
                    <span v-else-if="index == 'insurance'">
                        pour l'assurance omnium du vélo (vol et casse)</span
                    >
                    <span v-else-if="index == 'extensionWarranty'">
                        pour l'extension de garantie</span
                    >
                    <span v-else-if="index == 'rcInsurance'">
                        pour l'assurance RC</span
                    >
                    <span v-else-if="index == 'interest'">
                        pour les intérêts liés au financement du contrat (taux
                        annuel de 8%)</span
                    >
                    <span v-else-if="index == 'managementFee'">
                        pour la gestion du dossier</span
                    >
                </p>
            </div>
        </div>
        <p v-if="leasing_price.tvac?.residualValueAmount">
            <strong
                >Option d'achat en fin de contrat (16% de la valeur du
                vélo)</strong
            >
            :
            {{ formatPrice(leasing_price.tvac?.residualValueAmount) }}
        </p>
        <p v-else>
            Le vélo vous appartient dès la signature du crédit avec Cofidis.
        </p>
    </div>
</template>
